import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { DashboardView } from '@/domain/auth';
import { useUserController_UpdateProfile } from '@/services/hooks';

import { ME_QUERY_KEY, withCachedProfileField } from './meCache';

/**
 * Saves the location dashboard the user lands on. The cached `/me` response is updated first so
 * the switcher reflects the choice at once, then refetched once the request settles.
 */
export function useSaveDashboardView(): (view: DashboardView) => void {
    const queryClient = useQueryClient();
    const { mutate } = useUserController_UpdateProfile();

    return useCallback(
        (view: DashboardView) => {
            queryClient.setQueryData(ME_QUERY_KEY, (old: unknown) =>
                withCachedProfileField(old, 'defaultLocationDashboard', view)
            );
            mutate(
                { requestBody: { defaultLocationDashboard: view } },
                {
                    onSettled: () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
                }
            );
        },
        [mutate, queryClient]
    );
}
